import { useMemo } from 'react';

import { type QueryOptions } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';
import { grey, red } from '@mui/material/colors';
import type { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium';
import { MapPinIcon, MapPinOffIcon, Search } from 'lucide-react';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import {
  type GetOrganisationsListQuery,
  type GetOrganisationsListQueryVariables,
  Order_By,
  type Organisations_Bool_Exp,
  type Organisations_Order_By,
} from '@graphql/__generated__/graphql';
import { AddressCell } from '@modules/_shared/components/data-grid/AddressCell';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { OrganisationSelect } from '@modules/_shared/components/organisation-select/OrganisationSelect';
import { SmartListTitle } from '@modules/_shared/components/SmartListTitle';
import { TeamAvatar } from '@modules/_shared/components/TeamAvatar';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import {
  parseOrderByParam,
  withTiebreaker,
} from '@modules/_shared/utils/listUrlParams';
import { getOrganisationLogoUrl } from '@modules/_shared/utils/organisationLogo';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import { OrganisationCreateDrawer } from '@modules/organisations/OrganisationCreateDrawer';
import { OrganisationDrawer } from '@modules/organisations/OrganisationDrawer';
import {
  GET_ORGANISATIONS_LIST,
  GET_ORGANISATIONS_LIST_COUNT,
} from '@modules/organisations/organisationsQueries';
import { useLocale } from '@shared/hooks/locale';

type Organisation = GetOrganisationsListQuery['organisations'][number];

export const initialOrganisationsListQuery = (
  searchParams: URLSearchParams,
): QueryOptions<GetOrganisationsListQueryVariables> => {
  const where: Organisations_Bool_Exp = prepareWhereClauseQuery(
    JSON.parse(searchParams.get('where') || '{}'),
  );
  const order_by = withTiebreaker<Organisations_Order_By>(
    parseOrderByParam<Organisations_Order_By>(searchParams.get('order_by'), [
      { created_at: Order_By.Desc },
    ]),
    { id: Order_By.Asc },
    'id',
  );
  const { limit, offset } = getPaginationVariables(searchParams);

  return {
    query: GET_ORGANISATIONS_LIST,
    variables: { where, order_by, limit, offset },
  };
};

export const Organisations = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { variables } = useMemo(
    () => initialOrganisationsListQuery(searchParams),
    [searchParams],
  );

  const { data, loading, error } = useQuery(GET_ORGANISATIONS_LIST, {
    variables,
  });

  const organisations: GridRowsProp<Organisation> = data?.organisations ?? [];

  const { data: countData } = useQuery(GET_ORGANISATIONS_LIST_COUNT, {
    variables: {
      where: variables.where,
    },
  });

  const columns = useMemo<GridColDef<Organisation>[]>(
    () => [
      {
        field: 'created_at',
        headerName: t('Created'),
        width: 100,
        renderCell: ({ value }) => <TimeAgo dateString={value} />,
      },
      {
        field: 'name',
        headerName: t('Name'),
        minWidth: 250,
        maxWidth: 500,
        flex: 1,
        renderCell: ({ row }) => (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div style={{ position: 'relative' }}>
              <TeamAvatar logoUrl={getOrganisationLogoUrl(row)} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div>{row?.name}</div>
            </div>
          </div>
        ),
      },
      {
        field: 'postcode,locality,street,street_number',
        headerName: t('Address'),
        width: 200,
        display: 'flex',
        renderCell: ({ row }) => (
          <>
            {row.lat && row.lng ? (
              <MapPinIcon
                size={20}
                style={{ marginRight: 8, flexShrink: 0 }}
                color={red[700]}
              />
            ) : (
              <MapPinOffIcon
                size={20}
                style={{ marginRight: 8, flexShrink: 0 }}
                color={grey[300]}
              />
            )}
            <AddressCell
              address={{
                street_number: row.street_number ?? '',
                route: row.street ?? '',
                postcode: row.postcode ?? '',
                locality: row.locality ?? '',
              }}
            />
          </>
        ),
      },
      {
        field: 'teams_aggregate.count',
        headerName: t('Teams'),
        width: 100,
        align: 'right',
        valueGetter: (_, row) => row.teams_aggregate?.aggregate?.count ?? 0,
      },
    ],
    [t],
  );

  return (
    <>
      <SmartListTitle
        tableName="organisations"
        defaultTitle={t('Organisations')}
      />

      <ListToolbar
        tableName="organisations"
        quickFilters={[
          {
            label: t('Sales rep'),
            path: ['organisations_bool_exp', 'sales_rep'],
          },
          {
            label: t('CSM rep'),
            path: ['organisations_bool_exp', 'csm_rep'],
          },
        ]}
        newLink={{
          pathname: 'new',
          search: searchParams.toString(),
        }}
        searchComponent={
          <OrganisationSelect
            autoFocus
            InputProps={{
              startAdornment: <SelectStartIcon icon={Search} />,
            }}
            sx={{
              maxWidth: 350,
              flexGrow: 1,
              ml: 1,
              background: 'var(--mui-palette-background-paper)',
            }}
            onChange={organisationId => {
              if (organisationId) {
                navigate({
                  pathname: `/organisations/${organisationId}`,
                  search: searchParams.toString(),
                });
              }
            }}
          />
        }
      />
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : (
        <RaDataGrid
          loading={loading}
          rows={organisations}
          columns={columns}
          rowCount={countData?.organisations_aggregate.aggregate?.count ?? 0}
        />
      )}

      <Routes>
        <Route path="new" element={<OrganisationCreateDrawer />} />
        <Route path=":organisationId/*" element={<OrganisationDrawer />} />
      </Routes>
    </>
  );
};

export default Organisations;
